'use client';
import { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { routes } from '~constants/routes';
import { useTranslations } from '~i18n/hooks';
import PlusIcon from '~public/assets/icons/plus.svg';
import { ButtonSecondary } from '~shared/ui/atoms/Buttons/ButtonSecondary';
import { GridCarousel } from '~shared/ui/organisms/GridCarousel';

import { useComputeActiveDistance } from '../../hooks/useComputeActiveDistance';
import {
	Images,
	Info,
	StyledTitle,
	Tag,
	Tags,
	Video,
	Wrapper,
} from '../styles';
import { wrapperTransparentBackground } from './styles';

type Props = {
	imageUrl: string;
	videoUrl?: string;
	index: number;
	animated: boolean;
	// Указываем снаружи, потому что этой компоненты много инстансов, не хотим рассчитывать по 40 раз на одинвьюпорт
	mobileRow: number;
};

export const GridGenerateItem = ({
	imageUrl,
	videoUrl,
	index,
	animated,
	mobileRow,
}: Props) => {
	const t = useTranslations('Grid');
	const playable = animated && !!videoUrl;
	const scrollplay = playable && !!mobileRow;

	const videoRef = useRef<HTMLVideoElement>(null);

	const [viewRef, inView] = useInView({
		threshold: 0.25,
		skip: !scrollplay,
	});

	const handlePlay = () => {
		if (!videoRef.current || !videoRef.current.paused) {
			return;
		}
		void videoRef.current.play();
	};
	const handleStop = () => {
		if (!videoRef.current || videoRef.current.paused) {
			return;
		}
		void videoRef.current.pause();
		videoRef.current.currentTime = 0;
	};

	const { ref } = useComputeActiveDistance({
		index,
		inView,
		mobileRow,
		scrollplay,
		onChange: (active) => (active ? handlePlay() : handleStop()),
	});

	return (
		<Wrapper
			ref={ref}
			className={wrapperTransparentBackground}
			prefetch={false}
			href={routes.generate.character}
			onMouseEnter={handlePlay}
			onMouseLeave={handleStop}
		>
			<Images ref={viewRef}>
				{playable ? (
					<Video
						ref={videoRef}
						poster={imageUrl}
						preload="metadata"
						autoPlay={index === 0 && scrollplay}
						loop
						muted
						playsInline
						controls={false}
						disablePictureInPicture
						disableRemotePlayback
					>
						<source src={videoUrl} type="video/mp4" />
					</Video>
				) : (
					<GridCarousel fallbacks={[imageUrl]} />
				)}

				<StyledTitle size="M" data-name="generate-item-subtitle" align="center">
					{t('gridGenerateItemSubtitle')}
				</StyledTitle>
				<Tags>
					<Tag size="XS" data-name="generate-item-title">
						{t('gridGenerateItemTitle')}
					</Tag>
				</Tags>
			</Images>
			<Info>
				<ButtonSecondary
					size="M"
					leftIcon={<PlusIcon />}
					data-name="generate-item-button"
				>
					{t('gridGenerateItemButton')}
				</ButtonSecondary>
			</Info>
		</Wrapper>
	);
};
